import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import { useStateService } from '@/shared/services/StateService';
import SimplePanel from '@/shared/components/SimplePanel';
import LoadingContainer from '@/shared/components/loading/LoadingContainer';
import LoadingAnimation from '@/shared/components/LoadingAnimation';
import InfiniteScroll from 'react-infinite-scroller';
import { showWhenPermission } from '@/shared/components/security';
import { functionalities, privileges } from '@/shared/services/AuthService';
import { useRowSelection } from '@/shared/hooks/useRowSelection';
import dateFormatService from '@/shared/services/DateFormatService';
import { getPeriodDateFromUntil } from '@/shared/components/PeriodButtonGroup';
import userStorage from '@/shared/services/userStorage';
import { formatCount } from '@/shared/components/FormattedCurrency';
import { useGetOverdueInvoices, useGetRemindersOverviewInfo, useGetUpcomingReminders, useRefreshRemindersOverview, } from './hooks/useRemindersOverviewQueries';
import { ageingFromInfo, groupPerRelation, groupRemindersByInvoice, overdueListParams, sum, upcomingPerDay, } from './helpers';
import RemindersKpis from './RemindersKpis';
import AgeingBar from './AgeingBar';
import OverdueInvoiceFilters, { defaultOverdueFilters } from './OverdueInvoiceFilters';
import OverdueInvoicesTable from './OverdueInvoicesTable';
import OverdueRelationsTable from './OverdueRelationsTable';
import SentRemindersTab, { defaultSentFilters } from './SentRemindersTab';
import UpcomingRemindersTab, { UPCOMING_TAB_DAYS } from './UpcomingRemindersTab';
import CustomersTab from './CustomersTab';
import RemindersTabBar from './RemindersTabBar';
import messages from './messages';
const storageKey = 'RemindersOverviewFilters';
const tabStorageKey = 'RemindersOverviewTab';
const tabKeys = ['sent', 'upcoming', 'overdue', 'customers'];
const readStoredTab = () => {
    try {
        const stored = userStorage.getItem(tabStorageKey);
        return stored && tabKeys.includes(stored) ? stored : 'sent';
    }
    catch (_a) {
        return 'sent';
    }
};
const readStoredFilters = () => {
    try {
        const stored = sessionStorage.getItem(storageKey);
        return stored ? Object.assign(Object.assign({}, defaultOverdueFilters), JSON.parse(stored)) : defaultOverdueFilters;
    }
    catch (_a) {
        return defaultOverdueFilters;
    }
};
const RemindersOverviewContainer = ({ invoiceDialogsSvc }) => {
    var _a, _b, _c, _d;
    const intl = useIntl();
    const stateService = useStateService();
    const [activeTab, setActiveTab] = useState(readStoredTab);
    const [overdueFilters, setOverdueFilters] = useState(readStoredFilters);
    const [sentFilters, setSentFilters] = useState(defaultSentFilters);
    const { isSelected, toggleSelected, resetSelected, selectAll, setSelected, hasAnySelected, } = useRowSelection();
    const refresh = useRefreshRemindersOverview();
    const info = useGetRemindersOverviewInfo(overdueFilters);
    // Each tab loads its own data when it is opened; the header only needs the KPI info.
    const invoicesQuery = useGetOverdueInvoices(overdueFilters, activeTab === 'overdue');
    const invoices = useMemo(() => { var _a; return ((_a = invoicesQuery.data) === null || _a === void 0 ? void 0 : _a.pages.flatMap(p => p.data)) || []; }, [
        invoicesQuery.data,
    ]);
    const invoiceIds = useMemo(() => invoices.map(x => x.id), [invoices]);
    const reminders = useMemo(() => invoices.flatMap(x => x.reminders || []), [invoices]);
    const remindersByInvoice = useMemo(() => groupRemindersByInvoice(invoices), [invoices]);
    const mailSizes = useMemo(() => {
        const sizes = new Map();
        reminders.forEach(r => {
            if (r.emailId)
                sizes.set(r.emailId, (sizes.get(r.emailId) || 0) + 1);
        });
        return sizes;
    }, [reminders]);
    // Loaded with the page: the upcoming segment's subtitle comes from this feed.
    const upcomingQuery = useGetUpcomingReminders();
    const upcomingByInvoice = useMemo(() => {
        const map = new Map();
        (upcomingQuery.data || []).forEach(x => {
            const current = map.get(x.invoiceId);
            if (!current || x.date < current.date)
                map.set(x.invoiceId, x);
        });
        return map;
    }, [upcomingQuery.data]);
    useEffect(() => {
        try {
            userStorage.setItem(tabStorageKey, activeTab);
        }
        catch (_a) {
            // storage unavailable, the tab simply is not remembered
        }
    }, [activeTab]);
    useEffect(() => {
        sessionStorage.setItem(storageKey, JSON.stringify(overdueFilters));
        setSelected({});
    }, [overdueFilters, setSelected]);
    const updateOverdueFilters = useCallback((filters) => {
        setActiveTab('overdue');
        setOverdueFilters(current => {
            const next = Object.assign(Object.assign({}, current), filters);
            return JSON.stringify(current) === JSON.stringify(next) ? current : next;
        });
    }, []);
    const updateSentFilters = useCallback((filters) => {
        setSentFilters(current => (Object.assign(Object.assign({}, current), filters)));
    }, []);
    // One bucket at a time: the backend filter is a single days-overdue range.
    const toggleAgeBucket = (bucket) => updateOverdueFilters({
        ageBuckets: overdueFilters.ageBuckets.length === 1 && overdueFilters.ageBuckets[0] === bucket
            ? []
            : [bucket],
    });
    const groups = useMemo(() => groupPerRelation(invoices, remindersByInvoice, upcomingByInvoice), [
        invoices,
        remindersByInvoice,
        upcomingByInvoice,
    ]);
    const afterDialog = (dialog) => Promise.resolve(dialog)
        .catch(() => undefined)
        .finally(() => {
        refresh();
        resetSelected();
    });
    const onSendReminder = (invoice) => afterDialog(invoiceDialogsSvc.mailReminder(invoice));
    const onSendGroupedReminder = (selected) => {
        if (!selected.length)
            return;
        const languages = Array.from(new Set(selected.map(x => x.customer.languageCode).filter(Boolean)));
        const companyIds = new Set(selected.map(x => x.companyId));
        afterDialog(invoiceDialogsSvc.batchSendInvoices('email', overdueListParams(overdueFilters), { selectAll: false, invoiceIds: selected.map(x => x.id) }, languages, companyIds.size === 1 ? selected[0].companyId : null));
    };
    const onShowHistory = (invoice) => afterDialog(invoiceDialogsSvc.mailHistory(invoice));
    const onShowHistoryRow = (row) => afterDialog(invoiceDialogsSvc.mailHistory({ id: row.invoiceId, number: row.invoiceNr }));
    const onRegisterPayment = (invoice) => afterDialog(invoiceDialogsSvc.newInvoicePayment(invoice, null));
    const onOpenRelation = (relationId) => stateService.gotoRelation(relationId);
    const onOpenInvoice = (invoiceId) => { var _a; return (_a = stateService.$state) === null || _a === void 0 ? void 0 : _a.go('invoicing.overview', { invoiceId, showWrittenOffInternally: 2 }); };
    const onSelectMany = (ids, selected) => {
        if (selected) {
            selectAll(ids);
        }
        else {
            setSelected(current => {
                const next = Object.assign({}, current);
                ids.forEach(id => delete next[id]);
                return next;
            });
        }
    };
    const selectedInvoices = invoices.filter(x => isSelected(x.id));
    const upcomingInTab = useMemo(() => upcomingPerDay(upcomingQuery.data || [], UPCOMING_TAB_DAYS), [
        upcomingQuery.data,
    ]);
    const tabs = [
        {
            key: 'sent',
            icon: 'fa-paper-plane-o',
            title: intl.formatMessage(messages.SentRemindersTab),
            detail: intl.formatMessage(messages.TabDetailSent, {
                today: formatCount(((_a = info.data) === null || _a === void 0 ? void 0 : _a.remindersSentToday) || 0),
            }),
        },
        {
            key: 'upcoming',
            icon: 'fa-calendar-o',
            title: intl.formatMessage(messages.UpcomingRemindersTab),
            amount: upcomingQuery.data ? sum(upcomingInTab.map(d => d.amount)) : undefined,
            detail: !upcomingQuery.data
                ? undefined
                : upcomingInTab.length
                    ? intl.formatMessage(messages.TabDetailUpcoming, {
                        date: dateFormatService.formatDayMonth(upcomingInTab[0].date),
                        count: upcomingInTab[0].invoices.length,
                        formatted: formatCount(upcomingInTab[0].invoices.length),
                    })
                    : intl.formatMessage(messages.NothingUpcomingShort),
        },
        {
            key: 'overdue',
            icon: 'fa-hourglass-half',
            title: intl.formatMessage(messages.OverdueInvoicesTab),
            detail: intl.formatMessage(messages.TabDetailOverdue, {
                count: ((_b = info.data) === null || _b === void 0 ? void 0 : _b.notRemindedCount) || 0,
                formatted: formatCount(((_c = info.data) === null || _c === void 0 ? void 0 : _c.notRemindedCount) || 0),
            }),
            alert: (((_d = info.data) === null || _d === void 0 ? void 0 : _d.notRemindedCount) || 0) > 0,
        },
        {
            key: 'customers',
            icon: 'fa-users',
            title: intl.formatMessage(messages.CustomersTab),
        },
    ];
    return (_jsxs("div", Object.assign({ className: "reminders-overview" }, { children: [_jsx(SimplePanel, Object.assign({ article: "5434733497746", title: intl.formatMessage(messages.RemindersOverview), buttons: _jsxs("button", Object.assign({ type: "button", className: "btn btn-inverse btn-sm", onClick: () => { var _a; return (_a = stateService.$state) === null || _a === void 0 ? void 0 : _a.go('settings.reminder'); } }, { children: [_jsx("i", { className: "fa fa-cog margin5R" }), _jsx(FormattedMessage, Object.assign({}, messages.ReminderSettings))] })) }, { children: _jsx(LoadingContainer, Object.assign({ isLoading: info.isLoading }, { children: info.data && (_jsxs("div", Object.assign({ className: "reminders-header" }, { children: [_jsx(RemindersTabBar, { tabs: tabs, activeTab: activeTab, onChange: setActiveTab, vertical: true }), _jsx(AgeingBar, { totals: ageingFromInfo(info.data), selected: overdueFilters.ageBuckets, onToggle: toggleAgeBucket }), _jsx(RemindersKpis, { info: info.data, filters: overdueFilters, activeTab: activeTab, onOverdueFilter: updateOverdueFilters, onShowSentReminders: period => {
                                    setSentFilters(Object.assign(Object.assign({}, defaultSentFilters), getPeriodDateFromUntil(period)));
                                    setActiveTab('sent');
                                } })] }))) })) })), activeTab === 'overdue' && (_jsxs(_Fragment, { children: [_jsx(SimplePanel, Object.assign({ title: intl.formatMessage(messages.OverdueInvoicesTab) }, { children: _jsx("div", Object.assign({ className: "info-block container-fluid radius4B" }, { children: _jsx(OverdueInvoiceFilters, { filters: overdueFilters, onFilterChange: updateOverdueFilters }) })) })), _jsx("div", Object.assign({ className: "row btn-row" }, { children: _jsx("div", Object.assign({ className: "col-md-12 content-right" }, { children: _jsxs("button", Object.assign({ type: "button", className: "btn btn-inverse", disabled: !hasAnySelected || selectedInvoices.length === 0, onClick: () => onSendGroupedReminder(selectedInvoices) }, { children: [_jsx("i", { className: "fa fa-envelope-o margin5R" }), _jsx(FormattedMessage, Object.assign({}, messages.SendReminderToSelected, { values: { count: formatCount(selectedInvoices.length) } }))] })) })) })), _jsxs(LoadingContainer, Object.assign({ isLoading: invoicesQuery.isInitialLoading }, { children: [_jsx(InfiniteScroll, Object.assign({ pageStart: 0, loadMore: () => invoicesQuery.fetchNextPage(), hasMore: !!invoicesQuery.hasNextPage && !invoicesQuery.isFetchingNextPage, initialLoad: false, loader: null, threshold: 250 }, { children: overdueFilters.groupPerRelation ? (_jsx(OverdueRelationsTable, { groups: groups, mailSizes: mailSizes, remindersByInvoice: remindersByInvoice, upcoming: upcomingByInvoice, isSelected: isSelected, onToggleSelected: toggleSelected, onSelectMany: onSelectMany, onSendGroupedReminder: onSendGroupedReminder, onSendReminder: onSendReminder, onShowHistory: onShowHistory, onRegisterPayment: onRegisterPayment, onOpenInvoice: onOpenInvoice })) : (_jsx(OverdueInvoicesTable, { invoices: invoices, remindersByInvoice: remindersByInvoice, upcoming: upcomingByInvoice, isSelected: isSelected, onToggleSelected: toggleSelected, onToggleAllSelected: selected => selected ? selectAll(invoiceIds) : resetSelected(), onSendReminder: onSendReminder, onShowHistory: onShowHistory, onRegisterPayment: onRegisterPayment, onOpenInvoice: onOpenInvoice })) })), invoicesQuery.isFetchingNextPage && _jsx(LoadingAnimation, {})] }))] })), activeTab === 'sent' && (_jsx(SentRemindersTab, { filters: sentFilters, onFilterChange: updateSentFilters, onShowHistory: onShowHistoryRow, onOpenInvoice: onOpenInvoice })), activeTab === 'upcoming' && (_jsx(UpcomingRemindersTab, { upcoming: upcomingQuery.data, isLoading: upcomingQuery.isInitialLoading })), activeTab === 'customers' && _jsx(CustomersTab, { onOpenRelation: onOpenRelation })] })));
};
export default showWhenPermission(functionalities.InvoiceFinal, privileges.Read)(RemindersOverviewContainer);
